import { useParams } from "react-router-dom";
import { useCallback, useEffect, useState } from "react";
import { BulkSpareGallery } from "../../../../component/bulk/bulkSpares/bulkSparesDetail/BulkSpareGallery";
import {
  useCreateBulkSpareImageMutation,
  useDeleteBulkSpareImageMutation,
  useGetBulkSpareImagesQuery,
  useUpdateBulkSpareImageMutation,
} from "../../../../services/bulk/spares/bulkSpareImagesApiSlice";
import { v4 as uuidv4 } from "uuid";
import axiosInstance from "../../../../axios/axiosTokenMiddleware";
import { toast } from "react-toastify";

const uploadImageUrl =
  "https://dev.backend.mobigarage.com/v1/mp/accounts/upload_spare_bulk_deal_image?file_name=hmmm.zip";

/**
 * Upload file to storage server
 */
export const uploadImageRequest = async (file) => {
  if (!file) throw new Error("No file provided");

  const formData = new FormData();
  formData.append("file", file);

  const response = await axiosInstance.post(uploadImageUrl, formData);

  return response;
};

export const BulkSpareGalleryPage = () => {
  const { request_id } = useParams();

  const { data: imagesData } = useGetBulkSpareImagesQuery(request_id);

  const [createBulkSpareImage] = useCreateBulkSpareImageMutation();
  const [updateBulkSpareImage] = useUpdateBulkSpareImageMutation();
  const [deleteBulkSpareImage] = useDeleteBulkSpareImageMutation();

  const [images, setImages] = useState([]);

  /**
   * Initialize images (NO display_order logic for UI)
   */
  useEffect(() => {
    if (!imagesData) return;

    const formatted = imagesData.map((img) => ({
      id: img.id,
      url: img.url,
      is_thumbnail: img.is_thumbnail || 0,
      isUploading: false,
      progress: 100,
    }));

    setImages(formatted);
  }, [imagesData]);

  /**
   * Upload new image OR replace existing image
   */
  const uploadImage = useCallback(
    async (file, imageId = null) => {
      if (!file) return;

      let tempId = imageId || uuidv4();
      const tempPreview = URL.createObjectURL(file);

      let originalImage = null;

      /**
       * STEP 1: optimistic preview
       */
      setImages((prev) => {
        if (imageId) {
          originalImage = prev.find((img) => img.id === imageId);

          return prev.map((img) =>
            img.id === imageId
              ? {
                  ...img,
                  url: tempPreview,
                  isUploading: true,
                  progress: 10,
                }
              : img,
          );
        }

        return [
          ...prev,
          {
            id: tempId,
            url: tempPreview,
            is_thumbnail: 0,
            display_order: 0,
            isUploading: true,
            progress: 10,
          },
        ];
      });

      try {
        /**
         * STEP 2: upload to storage
         */
        const uploadResponse = await uploadImageRequest(file);

        const uploadedUrl = uploadResponse?.data?.fileUrl || null;

        if (!uploadedUrl) throw new Error("No fileUrl returned from upload");

        /**
         * STEP 3: update progress
         */
        setImages((prev) =>
          prev.map((img) =>
            img.id === tempId ? { ...img, progress: 60 } : img,
          ),
        );

        /**
         * STEP 4: save to backend
         */
        let backendId = imageId;

        if (imageId) {
          await updateBulkSpareImage({
            request_id,
            image_id: imageId,
            body: {
              url: uploadedUrl,
              is_thumbnail: originalImage?.is_thumbnail || 0,
              display_order: originalImage?.display_order || 0,
            },
          }).unwrap();
        } else {
          const postResponse = await createBulkSpareImage({
            request_id,
            body: {
              url: uploadedUrl,
              //   is_thumbnail: 0,
              //   display_order: 0,
            },
          }).unwrap();

          backendId = postResponse?.data?.id || tempId;
        }

        /**
         * STEP 5: finalize success
         */
        setImages((prev) =>
          prev.map((img) =>
            img.id === tempId
              ? {
                  ...img,
                  id: backendId,
                  url: uploadedUrl,
                  isUploading: false,
                  progress: 100,
                }
              : img,
          ),
        );

        URL.revokeObjectURL(tempPreview);
      } catch (error) {
        console.error("Upload failed:", error);

        /**
         * STEP 6: rollback
         */
        setImages((prev) => {
          if (imageId && originalImage) {
            return prev.map((img) =>
              img.id === imageId ? originalImage : img,
            );
          }

          return prev.filter((img) => img.id !== tempId);
        });

        URL.revokeObjectURL(tempPreview);
      }
    },
    [request_id, createBulkSpareImage, updateBulkSpareImage],
  );

  /**
   * Delete image
   */
  const handleDelete = useCallback(
    async (img) => {
      if (!img) return;

      const backup = img;

      setImages((prev) => prev.filter((image) => image.id !== img.id));

      try {
        const res = await deleteBulkSpareImage({
          request_id,
          image_id: img.id,
        }).unwrap();
        toast.success(
          res?.message?.displayMessage || "Image deleted successfully",
        );
      } catch (error) {
        
        toast.error(
          error?.data?.message?.displayMessage ||
            "Failed to delete image. Please try again.",
        );

        /**
         * rollback
         */
        setImages((prev) => [...prev, backup]);
      }
    },
    [deleteBulkSpareImage, request_id],
  );

  /**
   * Set primary image
   */
  const handlePrimary = async (img) => {
    try {
      const res = await updateBulkSpareImage({
        request_id,
        image_id: img.id,
        body: {
          url: img.url,
          is_thumbnail: 1,
        },
      }).unwrap();
      toast.success(
        res?.message?.displayMessage || "Image deleted successfully",
      );

      /**
       * update UI
       */
      setImages((prev) =>
        prev.map((image) => ({
          ...image,
          is_thumbnail: image.id === img.id ? 1 : 0,
        })),
      );
    } catch (error) {
      toast.error(
        error?.data?.message?.displayMessage ||
          "Failed to set primary image. Please try again.",
      );
    }
  };

  return (
    <BulkSpareGallery
      value={images}
      onUpload={(file) => uploadImage(file)}
      onReplace={(file, img) => uploadImage(file, img.id)}
      onDelete={handleDelete}
      onPrimary={handlePrimary}
    />
  );
};
